import { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { getManagedBilling } from 'api/insuranceBilling';
import { isPfSettingUp } from 'components/EhrConnect';
import type { ManagedBillingVo } from 'types/insuranceBilling';
import type { TEhrConnectionItem } from 'types/ehrConnection';

// 原型 CLAIM_OFF_REASONS
export const OTHERS_REASON = 'Others';
export const CLAIM_OFF_REASONS = [
    'I moved to another billing service',
    'I stopped billing insurance',
    'The fee is too high',
    'I am not seeing enough insured patients',
    OTHERS_REASON,
];

// Others 的备注上限 500（Q8b）：填了备注就原样存备注本身（与后端 reason 上限 500 一致），没填存「Others」
export const NOTE_MAX = 500;
export const composeReason = (reason: string, note: string): string => {
    const t = note.trim().slice(0, NOTE_MAX);
    return reason === OTHERS_REASON && t ? t : reason;
};

export const formatDay = (v?: string | null): string => (v ? dayjs(v).format('MMM D, YYYY') : '');

// AskChart 的 ehrs 是计费名单（billingEhrs），断开连接不会把 EHR 移出名单（有意保留原计费范围），
// 所以名单里可能有已经没有连接的 EHR。Managed billing 只展示当前有连接的（判定同 EHR tab 的连接行：
// 已连接 / PF 开通中 / 自建），只会少不会多，传给 pause/resume 的仍都在名单里
export const onlyConnectedEhrs = (state: ManagedBillingVo, connections: TEhrConnectionItem[]): ManagedBillingVo => {
    const listed = new Set(
        connections
            .filter((c) => c.connectionStatus === 'connected' || c.isCustom || isPfSettingUp(c))
            .map((c) => c.ehrSystem),
    );
    return { ...state, ehrs: state.ehrs.filter((e) => listed.has(e.ehr)) };
};

// 已停的 EHR：对所有人关掉的（Not billed）+ 只对部分 provider 关掉的（Not billed for N providers）
export const stoppedEhrs = (state: ManagedBillingVo) =>
    state.ehrs
        .filter((e) => !e.on || e.offForProviderIds.length > 0)
        .map((e) => {
            const k = e.offForProviderIds.length;
            return {
                ehr: e.ehr,
                label: !e.on ? 'Not billed' : `Not billed for ${k} ${k === 1 ? 'provider' : 'providers'}`,
            };
        });

// 拉 managed billing 状态（owner-only）；失败时 state 为 null，调用方按「不展示」处理
export function useManagedBilling(active: boolean) {
    const [state, setState] = useState<ManagedBillingVo | null>(null);
    const [loading, setLoading] = useState(false);

    const reload = useCallback(async () => {
        setLoading(true);
        const res: any = await getManagedBilling();
        setLoading(false);
        const vo: ManagedBillingVo | null = !res?.error ? (res?.data?.data ?? null) : null;
        setState(vo);
        return vo;
    }, []);

    useEffect(() => {
        if (active) reload();
    }, [active, reload]);

    return { state, setState, loading, reload };
}
